/**
 * Centralized keyboard shortcut configuration
 *
 * Uses 'mod' for cross-platform Cmd (Mac) / Ctrl (Windows/Linux) handling
 * react-hotkeys-hook automatically handles this translation
 */

export const HOTKEYS = {
  // Playback controls
  PLAY_PAUSE: 'space',
  PREVIOUS_FRAME: 'left',
  NEXT_FRAME: 'right',
  GO_TO_START: 'home',
  GO_TO_END: 'end',
  NEXT_SNAP_POINT: 'down',
  PREVIOUS_SNAP_POINT: 'up',

  // Timeline editing
  SPLIT_AT_PLAYHEAD: 'alt+c',
  JOIN_ITEMS: 'j',
  DELETE_SELECTED: 'delete',
  DELETE_SELECTED_ALT: 'backspace',
  RIPPLE_DELETE: 'mod+delete',
  RIPPLE_DELETE_ALT: 'mod+backspace',
  FREEZE_FRAME: 'shift+f',

  // History
  UNDO: 'mod+z',
  REDO: 'mod+y',

  // Zoom
  ZOOM_TO_FIT: 'z',
  ZOOM_TO_100: 'shift+z',

  // Clipboard
  COPY: 'mod+c',
  CUT: 'mod+x',
  PASTE: 'mod+v',

  // Tools
  SELECTION_TOOL: 'v',
  RAZOR_TOOL: 'c',
  SPLIT_AT_CURSOR: 'shift+c',
  RATE_STRETCH_TOOL: 'r',
  ROLLING_EDIT_TOOL: 'n',
  RIPPLE_EDIT_TOOL: 'b',
  SLIP_TOOL: 'y',
  SLIDE_TOOL: 'u',

  // Project
  SAVE: 'mod+s',
  EXPORT: 'mod+e',

  // UI
  TOGGLE_SNAP: 's',

  // Markers
  ADD_MARKER: 'm',
  REMOVE_MARKER: 'shift+m',
  PREVIOUS_MARKER: 'bracketleft',
  NEXT_MARKER: 'bracketright',

  // Keyframes
  ADD_KEYFRAME: 'k',
  CLEAR_KEYFRAMES: 'shift+k',
  TOGGLE_KEYFRAME_EDITOR: 'mod+k',

  // Track Groups
  GROUP_TRACKS: 'mod+g',
  UNGROUP_TRACKS: 'mod+shift+g',

  // Source Monitor
  MARK_IN: 'i',
  MARK_OUT: 'o',
  CLEAR_IN_OUT: 'alt+x',
  INSERT_EDIT: 'comma',
  OVERWRITE_EDIT: 'period',
} as const;

export type HotkeyKey = keyof typeof HOTKEYS;

/**
 * Human-readable descriptions for keyboard shortcuts
 * Used for tooltips, help dialogs, and documentation
 */
export const HOTKEY_DESCRIPTIONS: Record<HotkeyKey, string> = {
  // Playback
  PLAY_PAUSE: 'Воспроизвести/пауза',
  PREVIOUS_FRAME: 'Предыдущий кадр',
  NEXT_FRAME: 'Следующий кадр',
  GO_TO_START: 'Перейти в начало',
  GO_TO_END: 'Перейти в конец',
  NEXT_SNAP_POINT: 'Следующая опорная точка',
  PREVIOUS_SNAP_POINT: 'Предыдущая опорная точка',

  // Timeline editing
  SPLIT_AT_PLAYHEAD: 'Разрезать по позиции воспроизведения',
  JOIN_ITEMS: 'Объединить выбранные клипы',
  DELETE_SELECTED: 'Удалить выбранные объекты',
  DELETE_SELECTED_ALT: 'Удалить выбранные объекты (альтернативно)',
  RIPPLE_DELETE: 'Ripple Delete выбранные объекты',
  RIPPLE_DELETE_ALT: 'Ripple Delete выбранные объекты (альтернативно)',
  FREEZE_FRAME: 'Вставить стоп-кадр на позиции воспроизведения',

  // History
  UNDO: 'Отменить',
  REDO: 'Повторить',

  // Zoom
  ZOOM_TO_FIT: 'Масштабировать по всему содержимому',
  ZOOM_TO_100: 'Масштабировать до 100% по курсору/воспроизведению',

  // Clipboard
  COPY: 'Копировать выбранные объекты',
  CUT: 'Вырезать выбранные объекты',
  PASTE: 'Вставить объекты',

  // Tools
  SELECTION_TOOL: 'Инструмент выбора',
  RAZOR_TOOL: 'Инструмент резки',
  SPLIT_AT_CURSOR: 'Разрезать по курсору',
  RATE_STRETCH_TOOL: 'Инструмент изменения скорости',
  ROLLING_EDIT_TOOL: 'Инструмент риппл-редактирования',
  RIPPLE_EDIT_TOOL: 'Инструмент риппл правки',
  SLIP_TOOL: 'Инструмент сдвига',
  SLIDE_TOOL: 'Инструмент слайда',

  // Project
  SAVE: 'Сохранить проект',
  EXPORT: 'Экспортировать видео',

  // UI
  TOGGLE_SNAP: 'Переключить привязку',

  // Markers
  ADD_MARKER: 'Добавить маркер в позиции воспроизведения',
  REMOVE_MARKER: 'Удалить выбранный маркер',
  PREVIOUS_MARKER: 'Перейти к предыдущему маркеру',
  NEXT_MARKER: 'Перейти к следующему маркеру',

  // Keyframes
  ADD_KEYFRAME: 'Добавить ключевой кадр в позиции воспроизведения',
  CLEAR_KEYFRAMES: 'Очистить ключевые кадры выбранных объектов',
  TOGGLE_KEYFRAME_EDITOR: 'Переключить панель редактора ключевых кадров',

  // Track Groups
  GROUP_TRACKS: 'Сгруппировать выбранные дорожки',
  UNGROUP_TRACKS: 'Разгруппировать выбранные дорожки',

  // Source Monitor
  MARK_IN: 'Установить точку входа',
  MARK_OUT: 'Установить точку выхода',
  CLEAR_IN_OUT: 'Очистить точки входа/выхода',
  INSERT_EDIT: 'Вставить в позицию курсора',
  OVERWRITE_EDIT: 'Перезаписать',
};

/**
 * Options for react-hotkeys-hook
 * Prevents shortcuts from firing in input fields
 */
export const HOTKEY_OPTIONS = {
  enableOnFormTags: false, // Disable shortcuts when typing in inputs
  preventDefault: true, // Prevent default browser behavior
} as const;
